import { Fragment } from "react";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { formatDate, humanize } from "@/lib/format";

const STATE_TONE = { active: "success", grace: "warning", billing_issue: "warning", cancelled: "neutral", expired: "neutral" };
const money = (v) => (v != null ? `$${Number(v).toFixed(2)}` : "—");

function MockBadge({ mock, testId }) {
  return <Pill tone={mock ? "warning" : "success"} data-testid={testId}>{mock ? "Mock" : "Real"}</Pill>;
}

function Current({ sub }) {
  if (!sub) return <p className="cw-muted text-sm" data-testid="store-no-subscription">No live subscription.</p>;
  return (
    <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="store-current-subscription">
      <div><dt className="cw-muted text-xs">State</dt><dd className="mt-1 flex items-center gap-2"><Pill tone={STATE_TONE[sub.state] || "neutral"} className="capitalize">{humanize(sub.state)}</Pill><MockBadge mock={sub.mock} testId="store-sub-mock-badge" /></dd></div>
      <div><dt className="cw-muted text-xs">Product</dt><dd className="mt-1 font-mono text-xs">{sub.product_id}</dd><dd className="cw-soft text-xs capitalize">{sub.tier} · {sub.period}{sub.pending_tier ? ` → ${sub.pending_tier} next period` : ""}</dd></div>
      <div><dt className="cw-muted text-xs">Store</dt><dd className="mt-1 text-sm">{humanize(sub.store)}</dd></div>
      <div><dt className="cw-muted text-xs">{sub.renews ? "Renews" : "Period ends"}</dt><dd className="mt-1 text-sm">{formatDate(sub.period_end)}</dd>
        {sub.grace_until && <dd className="cw-warning text-xs">Grace until {formatDate(sub.grace_until)}</dd>}</div>
    </dl>
  );
}

function Events({ subs }) {
  const events = subs.flatMap((s) => (s.history || []).map((h) => ({ ...h, store: s.store, tier: s.tier }))).sort((a, b) => new Date(b.at) - new Date(a.at));
  if (!events.length) return <p className="cw-muted text-sm">No subscription events.</p>;
  return (
    <ul className="divide-y divide-border" data-testid="store-events">
      {events.slice(0, 50).map((h, i) => (
        <li key={i} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
          <span className="font-mono text-xs">{h.type}</span><span className="cw-soft flex-1">{h.product_id || "—"} · {humanize(h.store)}</span>
          <span className="cw-muted text-xs">{formatDate(h.at)}</span>
        </li>
      ))}
    </ul>
  );
}

function Purchases({ items }) {
  if (!items.length) return <p className="cw-muted text-sm">No purchases.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[680px] text-left text-sm" data-testid="store-purchases">
        <thead><tr className="cw-muted border-b border-border text-xs">
          <th className="py-2 pr-4 font-medium">When</th><th className="py-2 pr-4 font-medium">What</th><th className="py-2 pr-4 font-medium">Event</th>
          <th className="py-2 pr-4 font-medium">Store</th><th className="py-2 pr-4 font-medium">Price</th><th className="py-2 font-medium">Type</th>
        </tr></thead>
        <tbody className="divide-y divide-border">
          {items.map((p) => (
            <tr key={p.id} data-testid={`store-purchase-row-${p.id}`}>
              <td className="cw-soft whitespace-nowrap py-2.5 pr-4">{formatDate(p.created_at)}</td>
              <td className="py-2.5 pr-4">{p.kind === "pack" ? `Super Crush ×${p.quantity}` : <span className="capitalize">{p.tier} · {p.period}</span>}<span className="cw-muted block font-mono text-xs">{p.product_id}</span></td>
              <td className="py-2.5 pr-4 font-mono text-xs">{p.event || "—"}</td>
              <td className="py-2.5 pr-4">{humanize(p.store)}</td>
              <td className="py-2.5 pr-4 tabular-nums">{money(p.price_usd)}</td>
              <td className="py-2.5"><MockBadge mock={p.mock} testId={`store-purchase-mock-${p.id}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StoreSection({ store }) {
  const subs = store?.subscriptions || [];
  const blocks = [
    ["Current subscription", <Current key="c" sub={store?.summary?.subscription} />],
    ["Event history", <Events key="e" subs={subs} />],
    [`Purchase history (${(store?.purchases || []).length})`, <Purchases key="p" items={store?.purchases || []} />],
  ];
  return (
    <Panel className="space-y-6 p-6" data-testid="user-store-section">
      <h2 className="font-semibold">Store</h2>
      {blocks.map(([title, el]) => (
        <Fragment key={title}><section className="border-t border-border pt-5"><h3 className="mb-3 text-sm font-semibold">{title}</h3>{el}</section></Fragment>
      ))}
    </Panel>
  );
}
